import { ErrorFallback, Player, PlayerRef, RenderLoading, RenderPoster } from "@remotion/player";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { AbsoluteFill, OffthreadVideo } from "remotion";
import {
  CacheAfterError,
  GetHandleAudio,
  GetOrderedActionsList,
  GetPlayerError,
  GetPreviewIsPlaying,
  GetPreviewSrc,
  GetPreviewTime,
  GetSequenceDuration,
  GetSequenceIsPlaying,
  GetSequenceTime,
  GetShowSequence,
  ResetAfterError,
  SetIsFetching,
  SetPreviewIsPlaying,
  SetPreviewTime,
  SetSequenceIsPlaying,
  SetShowSequence,
} from "../../Redux/Reducers/redux-slice-timetool";
import { useDispatch } from "react-redux";
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import PreviewIcon from "@mui/icons-material/Preview";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import { VideoSequence } from "./video-sequence";
import { BufferManager } from "./BufferManager";
import { cacheService } from "../../Core/Api/services";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import TimetoolSequenceEditor from "./timetool-sequence-editor";
import { useTranslation } from "react-i18next";

const MS_BEFORE_RETRY_AFTER_ERROR = 3000;

const PlayerOnly: React.FC<{
  playerRef: React.RefObject<PlayerRef | null>;
  inputProps: Record<string, unknown>;
  clickToPlay: boolean;
  loop: boolean;
  durationInFrames: number;
  width: number;
  height: number;
  doubleClickToFullscreen: boolean;
  playbackRate: number;
  spaceKeyToPlayOrPause: boolean;
  moveToBeginningWhenEnded: boolean;
  showPosterWhenPaused: boolean;
  showPosterWhenEnded: boolean;
  showPosterWhenUnplayed: boolean;
  inFrame?: number | null;
  outFrame?: number | null;
  alwaysShowControls: boolean;
  showVolumeControls: boolean;
  showPlaybackRateControl: boolean | number[];
  fps: number;
  context: "preview" | "sequence" | "clip";
  controls: boolean;
  component?: any;
}> = ({
  playerRef,
  inputProps,
  clickToPlay,
  loop,
  durationInFrames,
  doubleClickToFullscreen,
  playbackRate,
  spaceKeyToPlayOrPause,
  moveToBeginningWhenEnded,
  showPosterWhenPaused,
  showPosterWhenEnded,
  showPosterWhenUnplayed,
  inFrame,
  outFrame,
  alwaysShowControls,
  showVolumeControls,
  showPlaybackRateControl,
  width,
  height,
  fps,
  context,
  controls,
  component,
  ...props
}) => {
  const { t } = useTranslation();
  const isPlaying = useSelector(GetSequenceIsPlaying);
  const previewIsPlaying = useSelector(GetPreviewIsPlaying);
  const showSequence = useSelector(GetShowSequence);
  const sequenceTime = useSelector(GetSequenceTime);
  const previewTime = useSelector(GetPreviewTime);
  const previewSrc = useSelector(GetPreviewSrc);
  const sequenceDuration = useSelector(GetSequenceDuration);
  const orderedActionsList = useSelector(GetOrderedActionsList);
  const playerError = useSelector(GetPlayerError);
  const handleAudio = useSelector(GetHandleAudio);

  const dispatch = useDispatch();
  const theme = useTheme();

  const [buffering, setBuffering] = useState(false);
  let pausedBecauseOfBuffering = useRef(false);

  useEffect(() => {
    if (playerRef.current && showSequence) {
      if (isPlaying) {
        playerRef.current.play();
      } else {
        playerRef.current.pause();
      }
    }
  }, [isPlaying, handleAudio]);

  useEffect(() => {
    if (playerRef.current && !showSequence) {
      if (previewIsPlaying) {
        playerRef.current.play();
      } else {
        playerRef.current.pause();
      }
    }
  }, [previewIsPlaying, handleAudio]);

  useEffect(() => {
    if (playerRef.current && showSequence) {
      playerRef.current.seekTo(Math.round(fps * sequenceTime));
      dispatch(SetSequenceIsPlaying(false));
    }
  }, [sequenceTime, handleAudio]);

  useEffect(() => {
    if (playerRef.current && !showSequence && !previewIsPlaying) {
      playerRef.current.seekTo(Math.round(fps * previewTime));
    }
  }, [previewTime, previewIsPlaying, handleAudio]);

  const playCallback = useCallback(() => {
    showSequence ? dispatch(SetSequenceIsPlaying(true)) : dispatch(SetPreviewIsPlaying(true));
  }, [showSequence, handleAudio]);

  const pauseCallback = useCallback(() => {
    !showSequence && dispatch(SetPreviewIsPlaying(false));
  }, [showSequence, handleAudio]);

  const seekedCallback = useCallback(
    (e) => {
      if (!showSequence) dispatch(SetPreviewTime(e.detail.frame / fps));
    },
    [showSequence, handleAudio]
  );

  const errorCallback = useCallback(
    (e) => {
      if (showSequence) {
        dispatch(SetSequenceIsPlaying(false));
      }
    },
    [showSequence, handleAudio]
  );

  const myComp = useCallback(
    () => (
      <OffthreadVideo
        onError={(e) => {
          dispatch(SetPreviewIsPlaying(false));
        }}
        src={`${previewSrc}/${cacheService.GetAuthKey()}` || ""}
      />
    ),
    [previewSrc, cacheService.GetAuthKey(), handleAudio]
  );

  useEffect(() => {
    playerRef.current?.addEventListener("play", playCallback);
    playerRef.current?.addEventListener("pause", pauseCallback);
    playerRef.current?.addEventListener("seeked", seekedCallback);
    playerRef.current?.addEventListener("error", errorCallback);

    return () => {
      playerRef.current?.removeEventListener("play", playCallback);
      playerRef.current?.removeEventListener("pause", pauseCallback);
      playerRef.current?.removeEventListener("seeked", seekedCallback);
      playerRef.current?.removeEventListener("error", errorCallback);
    };
  }, [playerRef.current, handleAudio]);

  const onError = useCallback(
    (e) => {
      setBuffering(true);
      dispatch(showSequence ? SetSequenceIsPlaying(false) : SetPreviewIsPlaying(false));
      dispatch(SetIsFetching(true));
      dispatch(CacheAfterError(e));
      setTimeout(() => {
        dispatch(ResetAfterError({}));
        dispatch(SetIsFetching(false));
        setBuffering(false);
      }, MS_BEFORE_RETRY_AFTER_ERROR);
    },
    [showSequence, handleAudio]
  );

  const onBuffer = useCallback(() => {
    setBuffering(true);
    pausedBecauseOfBuffering.current = true;
    dispatch(showSequence ? SetSequenceIsPlaying(false) : SetPreviewIsPlaying(false));
    dispatch(SetIsFetching(true));
  }, [showSequence, handleAudio]);

  const onContinue = useCallback(() => {
    setBuffering(false);
    if (pausedBecauseOfBuffering.current) {
      pausedBecauseOfBuffering.current = false;
      setTimeout(() => {
        dispatch(showSequence ? SetSequenceIsPlaying(true) : SetPreviewIsPlaying(true));
      }, 0);
      dispatch(SetIsFetching(false));
    }
  }, [showSequence, handleAudio]);

  const renderLoading: RenderLoading = useCallback(() => {
    return (
      <Stack
        sx={{ position: "relative", width: "100%" }}
        justifyContent="center"
        alignItems="center"
        textAlign="center"
        height={"100%"}
      >
        <CircularProgress
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
          }}
        />
      </Stack>
    );
  }, []);
  const renderPoster: RenderPoster = useCallback(() => {
    if (buffering) {
      return <></>;
    }
    return null;
  }, [buffering]);

  const errorFallback: ErrorFallback = useCallback(({ error }) => {
    return (
      <AbsoluteFill
        style={{
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {t("page:body.anErrorOccurred", { message: error.message })}
      </AbsoluteFill>
    );
  }, []);

  const ErrorMemo = useMemo(
    () => (
      <Box
        position={"absolute"}
        display={"flex"}
        justifyContent={"center"}
        alignContent={"center"}
        alignItems={"center"}
        sx={{
          width: "40%",
          top: "28%",
          left: "29%",
          zIndex: 1000,
          borderRadius: 2,
          padding: 3,
          backgroundColor: theme.palette.background[400],
        }}
      >
        <Grid container alignItems="center">
          <Grid item xs={3}>
            <CircleIcon alignItems="center" justifyContent="center" bgcolor={`error.main`}>
              <ErrorOutlineIcon fontSize="large" color="warning" />
            </CircleIcon>
          </Grid>
          <Grid item xs={9}>
            <Stack>
              <Typography fontWeight={500} sx={{ overflowWrap: "break-word", maxHeight: "17vh", overflowY: "hidden" }}>
                {t("page:body.errorWhilePlayingVideo")}
              </Typography>

              <Typography variant="body2" color="text.tier" sx={{ overflowWrap: "break-word" }}>
                {playerError}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    ),
    [playerError]
  );

  const HeaderMemo = useMemo(
    () => (
      <Box
        sx={{
          backgroundColor: theme.palette.background[200],
          position: "relative",
          top: "0px",
          height: "36px",
          zIndex: 1000,
          opacity: 0.6,
        }}
      >
        <Chip
          sx={{ width: "92px", ml: 1 }}
          label={showSequence ? "Sequence" : "Preview"}
          color={showSequence ? "primary" : "secondary"}
        />
        <Tooltip title={t("tooltip:title.showPreviewPlayer")}>
          <IconButton
            disabled={!showSequence || previewSrc === "" || !previewSrc}
            onClick={() => dispatch(SetShowSequence(false))}
          >
            <PreviewIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("tooltip:title.showSequncePlayer")}>
          <IconButton
            disabled={showSequence || !(sequenceDuration > 0)}
            onClick={() => {
              dispatch(SetPreviewIsPlaying(false));
              dispatch(SetShowSequence(true));
            }}
          >
            <ClearAllIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    [showSequence, previewSrc, sequenceDuration]
  );

  const ControlsMemo = useMemo(
    () => (
      <Box
        sx={{
          backgroundColor: theme.palette.background[200],
          width: "100%",
          position: "relative",
          flex: 1.5,
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignContent: "center",
        }}
      >
        <TimetoolSequenceEditor
          sx={{ display: "flex", flexDirection: "row", justifyContent: "center", alignContent: "center" }}
        />
      </Box>
    ),
    []
  );

  return (
    <Paper
      sx={{
        overflowY: "scroll",
        scrollbarGutter: "stable",
        padding: 0,
        display: component ? "none" : "flex",
        width: "100%",
        height: "100%",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {HeaderMemo}
      {durationInFrames > 1 && playerError === null && (
        <BufferManager onError={onError} onBuffer={onBuffer} onContinue={onContinue}>
          <Player
            component={showSequence ? VideoSequence : myComp}
            style={{ width: "100%", zIndex: 1200 }}
            ref={playerRef as any}
            controls={controls}
            showVolumeControls={showVolumeControls}
            compositionWidth={width}
            compositionHeight={height}
            fps={fps}
            durationInFrames={durationInFrames}
            doubleClickToFullscreen={doubleClickToFullscreen}
            loop={loop}
            initiallyMuted={component ? true : false}
            clickToPlay={clickToPlay}
            inputProps={inputProps}
            errorFallback={errorFallback}
            playbackRate={playbackRate}
            spaceKeyToPlayOrPause={spaceKeyToPlayOrPause}
            moveToBeginningWhenEnded={moveToBeginningWhenEnded}
            renderPoster={renderPoster}
            renderLoading={renderLoading}
            initialFrame={0}
            showPosterWhenUnplayed={false}
            showPosterWhenEnded={false}
            showPosterWhenPaused={false}
            inFrame={inFrame}
            outFrame={outFrame}
            alwaysShowControls={alwaysShowControls}
            showPlaybackRateControl={showPlaybackRateControl}
          />
        </BufferManager>
      )}
      {sequenceTime <= sequenceDuration &&
        orderedActionsList.length > 0 &&
        orderedActionsList.find((x) => x.start < sequenceTime && x.end > sequenceTime)?.data.src !== "" &&
        (playerError === null ? (
          <CircularProgress sx={{ position: "absolute", top: "47%", left: "47%", zIndex: 1000 }} />
        ) : (
          ErrorMemo
        ))}
      {!showSequence && durationInFrames > 1 && playerError === null && ControlsMemo}
    </Paper>
  );
};

export default PlayerOnly;

const CircleIcon = styled(Stack)(({ theme }) => ({
  width: 28,
  height: 28,
  borderRadius: 28,
  ".MuiSvgIcon-root": {
    fontSize: 16,
    color: theme.palette.background.paper,
  },
}));
